import { z } from 'zod'

/**
 * Типы событий, которые показывает журнал. Имена — как у бэкенда. Три из ТЗ п. 7 и охрана
 * (`security_overstay`, с 30.09.2026 у бэкенда, в журнале с 21.10.2026).
 */
export const EVENT_TYPES = [
  'conflict',
  'smoke_fire',
  'cashier_missing',
  'security_overstay',
] as const
export const eventTypeSchema = z.enum(EVENT_TYPES)
export type EventType = z.infer<typeof eventTypeSchema>

/** Тип события в интерфейсе: известный или 'unknown', чтобы новый тип на бэкенде не ломал журнал. */
export type EventKind = EventType | 'unknown'
export const isEventType = (value: string): value is EventType =>
  (EVENT_TYPES as readonly string[]).includes(value)

/** Событие — как в internal_api.EventResponse бэкенда. */
export const eventSchema = z.object({
  /** Например CSA-20260904-CAMCASHDESK1-56b5cd446a35. */
  id: z.string(),
  type: z.string().transform((value): EventKind => (isEventType(value) ? value : 'unknown')),
  /** Подпись от бэкенда: «Дым», «Конфликт», … */
  label: z.string().optional(),
  /** Уточнение для дыма/возгорания: smoke | fire. */
  alert_class: z.string().optional(),
  camera_id: z.string(),
  camera_ip: z.string().optional(),
  detected_at: z.string(),
  confidence: z.number().optional(),
  /** Есть хоть какое-то медиа (снимок или клип). */
  has_media: z.boolean().optional(),
  /** Что именно отдаст ?kind=image / ?kind=video. Старые ответы без этих полей — см. hasEventPhoto/hasEventVideo. */
  has_photo: z.boolean().optional(),
  has_video: z.boolean().optional(),
  /** Путь до медиа (абсолютный, если у бэкенда настроен PUBLIC_BASE_URL); kind=image|video добавляется запросом. */
  media_url: z.string().optional(),
  cash_desk_number: z.number().optional(),
  absence_started_at: z.string().optional(),
  absence_seconds: z.number().optional(),
  /** Охрана: номер зоны поста, начало и длительность (порог бэкенда — 30 минут). */
  guard_zone_number: z.number().optional(),
  guard_incident_started_at: z.string().optional(),
  guard_incident_seconds: z.number().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})
export type Event = z.infer<typeof eventSchema>

/** Ответ GET /events — плоский, без обёртки. */
export const eventsPageSchema = z.object({
  data: z.array(eventSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
})
export type EventsPage = z.infer<typeof eventsPageSchema>
